import { GridWidgetTask } from './grid-widget-task';
import { RestArgument } from './rest-argument';
export class PaginateTask extends GridWidgetTask {

  /**
   * The name of the task. Something like 'Filter', 'Paginate', or 'Sort'
   */
  name = 'paginate';

  // UI Config Options
  showNextPageInput = true;

  hidePagination = false;

  showRowsPerPage = true;
  // End UI Config Options

  /**
   * The results of the last run will be stored here.
   */
  result: Array<any>;

  //Number of Rows is how many items are in the entire selection of data.
  numberOfRows = 0;

  //Number of Rows that are in the current page.
  //NOTE: This is just for the UI to know how many are there whether in client side mode or server side mode.
  numberOfRowsInPage: number;

  //The current page you are looking at. This controlled by the grid widget. Do not directly modify outside of grid widget.
  pageNumber = 1;

  //The number of rows that will show up on the page at a time
  rowsPerPage = 10;

  // maximum rows per page allowed (more than this can have performance issue on client and also named query support)
  rowsPerPageMax = 1000;

  rowsPerPageMaxLimitExceeded = false;

  // an array of page numbers
  pages: Array<number> | undefined = undefined;

  // value in pagination controller input field for nextPageNumber;
  //This is the page number that we will go to next time the pagination
  //runs as opposed to this.pageNumber which describes the current page
  //that the filter is on.
  nextPageNumber = 1;

  numberOfPages: number;

  firstInListNumber: number;

  getPriority(): number {
    return GridWidgetTask.PAGINATE_TASK_PRIORITY;
  }

  /**
   * Sets the number or rows that are in the entire (pre-paginated) selection of data.
   * In server side mode this should be updated every time a page worth of data gets fetched.
   * NOTE: This is used for both serverSide Mode (When the data comes back)
   * and for Client Side mode in the run method.
   * @param numberOfRows - integer greater than or equal to zero.
   */
  setNumberOfRows(numberOfRows: number): void {
    this.numberOfRows = numberOfRows;
    this.updateNumberOfPages();
    this.updatePaginationNumbers();
  }

  /**
   * Sets the number of rows that are showing up in the UI.
   * This needs to be set manually when in server side mode.
   * @param numberOfRowsInPage
   */
  setNumberOfRowsInPage(numberOfRowsInPage: number): void {
    this.numberOfRowsInPage = numberOfRowsInPage;
  }

  //Use from outside the grid widget to set the page number.
  setPageNumber(pageNumber: number): void {
    this.pageNumber = pageNumber;
    this.nextPageNumber = pageNumber;
  }

  /**
   * Gets the page number that is being requested.
   * IE: The user just clicked page 3, this function will return 3.
   * @returns integer - greater than 0.
   */
  getPageNumber = function (): number {
    return this.pageNumber;
  };

  /**
   * Gets the number of rows to display per page. This comes from the user.
   * @returns integer - The number of rows per page.
   */
  getRowsPerPage = function (): number {
    return this.rowsPerPage;
  };

  hidePageControls(): boolean {
    return this.hidePagination || this.rowsPerPage === -1 || this.numberOfRows === 0;
  }

  /**
   * Sets the page number to the first page
   * and calls refreshPagination() of the outside controller.
   * Called by the pagination controls on the page.
   */
  firstPage = function (): void {
    if (this.pageNumber === 1) {
      return;
    }
    this.nextPageNumber = 1;
    this.execute();
  };

  /**
   * Sets the page number to the previous page (if possible)
   * and calls refreshPagination() of the outside controller.
   * Called by the pagination controls on the page.
   */
  previousPage = function (): void {
    if (this.pageNumber > 1) {
      this.nextPageNumber = this.pageNumber - 1;
    } else {
      return;
    }
  };

  /**
   * Sets the page number to the next page (if possible)
   * and calls refreshPagination() of the outside controller.
   * Called by the pagination controls on the page.
   */
  nextPage = function (): void {
    if (this.pageNumber < this.numberOfPages) {
      this.nextPageNumber = this.pageNumber + 1;
    } else {
      return;
    }
  };

  /**
   * Sets the page number to the last page
   * and calls refreshPagination() of the outside controller.
   * Called by the pagination controls on the page.
   */
  lastPage = function (): void {
    if (this.pageNumber === this.numberOfPages) {
      return;
    }
    this.nextPageNumber = this.numberOfPages;
  };

  /**
   * Sets the page number and calls refreshPagination() of the outside controller.
   * Called by the pagination controls on the page.
   */
  setPage = function (pageNumber): void {
    if (pageNumber >= 1 &&
      pageNumber <= this.numberOfPages &&
      pageNumber !== this.pageNumber) {
      this.nextPageNumber = pageNumber;
      this.execute();
    }
  };

  taskChainCanceled(): void {
    //If the task chain was canceled, we need to set the UI page number back to what it really is.
    this.nextPageNumber = this.pageNumber;
  }

  preRunSetup(): void {
    //This will happen whether in server side mode or not.
    this.pageNumber = this.nextPageNumber;

    if (!this.gridWidgetTaskActive) {
      this.restArguments = [
        new RestArgument('page', '=', this.pageNumber),
        new RestArgument('page-size', '=', this.rowsPerPage)
      ];
    }
  }

  /**
   * The main function to exectute.
   */
  run(dataRows: Array<any>): Array<any> {
    if (dataRows == null) {
      return [];
    }

    this.setNumberOfRows(dataRows.length);

    //Core Pagination Work
    let pageData: Array<any> = [];
    if (dataRows && dataRows.length > 0) {
      for (let i = (this.pageNumber - 1) * this.rowsPerPage;
        i < this.pageNumber * this.rowsPerPage && i < dataRows.length; i++) {
        pageData.push(dataRows[i]);
      }
    }
    //Now that we have our page of data, cache the number of rows in the page.
    this.numberOfRowsInPage = pageData.length;

    //End Core Pagination Work
    return pageData;
  }

  /**
   * You MUST be able to call JSON.stringify on the returned object.
   */
  getPersistenceObject(): Object | undefined {
    if (this.pageNumber > 1) {
      return this.pageNumber;
    }
  }

  restoreFromPersistenceObject(value: Object | undefined): void {
    if (typeof value !== 'number') {
      return;
    }

    let pageNumberString = <number>value;
    if (pageNumberString) {
      let pageNumber = Number(pageNumberString);
      this.setPageNumber(pageNumber);
    }
  }

  private validateRowsPerPage(): void {
    if (this.rowsPerPage == null) {
      this.rowsPerPage = 10;
    } else if (typeof this.rowsPerPage === 'string') {
      //If the grid widget is hooked up right, this should never
      //be a string, but this is good while the number widget is still
      //new.
      this.rowsPerPage = parseInt(String(this.rowsPerPage));
    }

    if (isNaN(this.rowsPerPage) || this.rowsPerPage <= 0) {
      this.rowsPerPage = 10;
    }

    if (this.rowsPerPage > this.rowsPerPageMax) {
      this.rowsPerPage = this.rowsPerPageMax;
      this.rowsPerPageMaxLimitExceeded = true;
    }
  }

  private validatePageNumber(): void {
    if (this.pageNumber == null) {
      this.pageNumber = 1;
    } else if (typeof this.pageNumber === 'string') {
      this.pageNumber = Math.abs(this.pageNumber);
    }

    if (isNaN(this.pageNumber)) {
      this.pageNumber = 1;
    }

    if (this.pageNumber > 1 && this.numberOfRows > 0) {
      let totalPages = Math.ceil(this.numberOfRows / this.rowsPerPage);
      if (this.pageNumber > totalPages) {
        this.pageNumber = totalPages;
        this.nextPageNumber = this.pageNumber;
      }
    }

    if (this.pageNumber === 0) {
      this.pageNumber = 1;
    }
  }

  /**
   * Calculates the number of pages based on numberOfRows and rowsPerPage.
   * Also makes sure the current page is within bounds.
   */
  private updateNumberOfPages(): void {
    this.validateRowsPerPage();
    this.validatePageNumber();

    let numberOfPages = Math.ceil(this.numberOfRows / this.rowsPerPage);

    //When the number of pages changes, we will also make sure the current page is within bounds.
    if (this.pageNumber > numberOfPages && numberOfPages > 0) {
      this.pageNumber = numberOfPages;
    } else if (this.pageNumber <= 0) {
      this.pageNumber = 1;
    }

    if (parseInt(String(this.nextPageNumber)) !== this.pageNumber) {
      this.nextPageNumber = this.pageNumber;
    }

    if (numberOfPages !== this.numberOfPages) {
      this.numberOfPages = numberOfPages;
    }
  }

  /**
   * Updates the numbers that show up in the pagination controls at the bottom
   * of the widget.
   */
  private updatePaginationNumbers(): void {
    if (this.pageNumber < 1 || this.pageNumber > this.numberOfPages) {
      return;
    }
    let maxPages = 5;
    let numToDisplay = Math.min(maxPages, this.numberOfPages);
    let i;

    this.pages = [];

    this.firstInListNumber = (this.pageNumber - 1) * this.rowsPerPage + 1;

    if (numToDisplay < maxPages) { //Easy, show them all
      for (i = 1; i < numToDisplay + 1; i++) {
        this.pages.push(i);
      }
    } else {
      this.pages.push(this.pageNumber);
      let low = this.pageNumber;
      let high = this.pageNumber;
      let rightOnly = low === 1;
      let leftOnly = high === this.numberOfPages;

      for (i = 1; i < numToDisplay; i++) {
        if ((i % 2 === 0 || leftOnly) && !rightOnly) {
          //try the left first
          this.pages.unshift(--low);
          rightOnly = low === 1;
        } else {
          //try the right first
          this.pages.push(++high);
          leftOnly = high === this.numberOfPages;
        }
      }
    }
  }
}
